import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "./CurrencyMasterPage";
import { Field } from "./BankAccountsPage";
import { SegBadge } from "./OpeningBalancesPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { useAppCtx } from "../contexts/AppContext";
import { PeriodFilter, periodParams } from "../components/PeriodFilter";
import { PartnerAutocomplete } from "../components/PartnerAutocomplete";
import { toast } from "sonner";
import { Trash2, Pencil } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

// Voice segment settles in USD, SMS in EUR. Used to render a market-standard
// FX hint ("X <cur> = 1 <base>") on the voucher form.
const SEGMENT_BASE = { Voice: "USD", SMS: "EUR" };

function VoucherPage({ kind }) {
    const isReceipt = kind === "receipts";
    const path = isReceipt ? "/receipts" : "/payments";
    const numField = isReceipt ? "receipt_number" : "payment_number";
    const titleSingular = isReceipt ? "Receipt" : "Payment";
    const eyebrow = isReceipt ? "Inwards" : "Outwards";
    const moduleKey = isReceipt ? "receipts" : "payments";

    const { hasPerm } = useAuth();
    const canWrite = hasPerm(moduleKey, "write");
    const { entities, currencies, selectedEntity, selectedSegment } = useAppCtx();
    const [banks, setBanks] = useState([]);
    const [overheadLedgers, setOverheadLedgers] = useState([]);
    const [generalLedgers, setGeneralLedgers] = useState([]);
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(blank());
    const [editId, setEditId] = useState(null);
    const [period, setPeriod] = useState({ from: null, to: null, preset: "this_month" });

    function blank() {
        return {
            entity_id: "", segment: "Voice", [numField]: "", date: today(),
            bank_account_id: "",
            // Both receipts & payments now support a payee_type toggle.
            //   receipts:  'partner' | 'gl'
            //   payments:  'partner' | 'overhead' | 'gl'
            payee_type: "partner",
            partner_name: "",
            overhead_ledger_id: "",
            general_ledger_id: "",
            currency: "USD", amount: "", fx_rate: "", remarks: "",
            bank_date: "",
        };
    }

    const startEdit = (r) => {
        setEditId(r.id);
        const ptype = r.general_ledger_id ? "gl"
            : r.overhead_ledger_id ? "overhead"
            : "partner";
        setForm({
            entity_id: r.entity_id || "",
            segment: r.segment || "Voice",
            [numField]: r[numField] || "",
            date: r.date || today(),
            bank_account_id: r.bank_account_id || "",
            payee_type: ptype,
            partner_name: r.partner_name || "",
            overhead_ledger_id: r.overhead_ledger_id || "",
            general_ledger_id: r.general_ledger_id || "",
            currency: r.currency || "USD",
            amount: String(r.amount ?? ""),
            fx_rate: r.fx_rate ? String(r.fx_rate) : "",
            remarks: r.remarks || "",
            bank_date: r.bank_date || "",
        });
        setOpen(true);
    };

    const closeDialog = (next) => {
        setOpen(next);
        if (!next) {
            setEditId(null);
            setForm(blank());
        }
    };

    const load = async () => {
        const params = { ...periodParams(period) };
        if (selectedEntity !== "all") params.entity_id = selectedEntity;
        if (selectedSegment !== "all") params.segment = selectedSegment;
        const calls = [api.get(path, { params }), api.get("/banks"), api.get("/general-ledgers")];
        if (!isReceipt) {
            calls.push(api.get("/overhead-ledgers"));
        }
        const results = await Promise.all(calls);
        setRows(results[0].data);
        setBanks(results[1].data);
        setGeneralLedgers(results[2].data || []);
        if (!isReceipt) {
            setOverheadLedgers(results[3].data || []);
        }
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [selectedEntity, selectedSegment, period]);

    const filteredBanks = form.entity_id ? banks.filter((b) => b.entity_id === form.entity_id) : banks;

    const submit = async () => {
        const payeeOk = form.payee_type === "partner"
            ? !!form.partner_name
            : form.payee_type === "overhead"
              ? !!form.overhead_ledger_id
              : !!form.general_ledger_id;
        if (!form.entity_id || !form.bank_account_id || !payeeOk || !form.amount) {
            const types = isReceipt ? "partner / GL" : "partner / overhead / GL";
            toast.error(`Entity, bank, source (${types}) and amount are required`);
            return;
        }
        try {
            const body = {
                entity_id: form.entity_id,
                segment: form.segment,
                [numField]: form[numField] || null,
                date: form.date,
                bank_account_id: form.bank_account_id,
                currency: form.currency,
                amount: parseFloat(form.amount),
                fx_rate: form.fx_rate ? parseFloat(form.fx_rate) : null,
                remarks: form.remarks,
                bank_date: form.bank_date || null,
            };
            if (form.payee_type === "partner") {
                body.partner_name = form.partner_name;
            } else if (form.payee_type === "overhead") {
                body.overhead_ledger_id = form.overhead_ledger_id;
            } else {
                body.general_ledger_id = form.general_ledger_id;
            }
            if (editId) {
                await api.put(`${path}/${editId}`, body);
                toast.success(`${titleSingular} updated`);
            } else {
                await api.post(path, body);
                toast.success(`${titleSingular} created`);
            }
            closeDialog(false);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm(`Delete this ${titleSingular.toLowerCase()}?`)) return;
        await api.delete(`${path}/${id}`);
        await load();
    };

    const entityName = (id) => entities.find((e) => e.id === id)?.name || "—";
    const bankName = (id) => banks.find((b) => b.id === id)?.name || "—";
    const bankLabel = (b) => {
        const ent = entities.find((e) => e.id === b.entity_id)?.name;
        return `${ent ? ent + " — " : ""}${b.name} (${b.currency})`;
    };

    return (
        <div data-testid={`${moduleKey}-page`}>
            <PageHeader
                title={isReceipt ? "Receipts (Inwards)" : "Payments"}
                eyebrow={eyebrow}
                description={isReceipt ? "Customer receipts into bank accounts." : "Outflows to vendors / partners from bank accounts."}
                action={
                    canWrite ? (
                        <Dialog open={open} onOpenChange={closeDialog}>
                            <DialogTrigger asChild>
                                <Button className="rounded-sm bg-[#002FA7] hover:bg-[#001f6f]" data-testid={`${moduleKey}-add-btn`}>
                                    + New {titleSingular.toLowerCase()}
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="rounded-sm max-w-xl">
                                <DialogHeader>
                                    <DialogTitle>{editId ? `Edit ${titleSingular}` : `New ${titleSingular}`}</DialogTitle>
                                </DialogHeader>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Field label="Entity">
                                        <Select value={form.entity_id} onValueChange={(v) => setForm({ ...form, entity_id: v, bank_account_id: "" })}>
                                            <SelectTrigger className="rounded-sm" data-testid={`${moduleKey}-entity-select`}><SelectValue placeholder="Select entity"/></SelectTrigger>
                                            <SelectContent>{entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Segment">
                                        <Select value={form.segment} onValueChange={(v) => setForm({ ...form, segment: v })}>
                                            <SelectTrigger className="rounded-sm" data-testid={`${moduleKey}-segment-select`}><SelectValue/></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="Voice">Voice (USD)</SelectItem>
                                                <SelectItem value="SMS">SMS (EUR)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label={`${titleSingular} # (auto)`}>
                                        <Input value={form[numField]} onChange={(e) => setForm({ ...form, [numField]: e.target.value })} className="rounded-sm h-9 font-mono" data-testid={`${moduleKey}-number-input`}/>
                                    </Field>
                                    <Field label="Date">
                                        <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm h-9 font-mono" data-testid={`${moduleKey}-date-input`}/>
                                    </Field>
                                    <Field label={isReceipt ? "Receiving bank" : "Source bank"}>
                                        <Select value={form.bank_account_id} onValueChange={(v) => {
                                            const b = banks.find((x) => x.id === v);
                                            setForm({ ...form, bank_account_id: v, currency: b?.currency || form.currency, fx_rate: b && b.currency === SEGMENT_BASE[form.segment] ? "" : form.fx_rate });
                                        }}>
                                            <SelectTrigger className="rounded-sm" data-testid={`${moduleKey}-bank-select`}><SelectValue placeholder="Select bank"/></SelectTrigger>
                                            <SelectContent>
                                                {filteredBanks.length === 0 && <div className="text-[12px] text-slate-500 px-3 py-2">No banks for entity</div>}
                                                {filteredBanks.map((b) => <SelectItem key={b.id} value={b.id}>{bankLabel(b)}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label={isReceipt ? "Receive from" : "Pay to"}>
                                        <Select
                                            value={form.payee_type}
                                            onValueChange={(v) => setForm({ ...form, payee_type: v, partner_name: "", overhead_ledger_id: "", general_ledger_id: "" })}
                                        >
                                            <SelectTrigger className="rounded-sm" data-testid={`${moduleKey}-payee-type-select`}><SelectValue/></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="partner">Partner</SelectItem>
                                                {!isReceipt && <SelectItem value="overhead">Overhead Ledger</SelectItem>}
                                                <SelectItem value="gl">General Ledger</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    {form.payee_type === "partner" ? (
                                        <Field label="Partner">
                                            <PartnerAutocomplete
                                                value={form.partner_name}
                                                onChange={(v) => setForm({ ...form, partner_name: v })}
                                                placeholder="Type to search partner…"
                                                testId={`${moduleKey}-partner-input`}
                                            />
                                        </Field>
                                    ) : form.payee_type === "overhead" ? (
                                        <Field label="Overhead Ledger">
                                            <Select
                                                value={form.overhead_ledger_id}
                                                onValueChange={(v) => setForm({ ...form, overhead_ledger_id: v })}
                                            >
                                                <SelectTrigger className="rounded-sm" data-testid="payments-overhead-ledger-select">
                                                    <SelectValue placeholder="Select ledger"/>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {overheadLedgers.filter((l) => l.active).length === 0 && (
                                                        <div className="text-[12px] text-slate-500 px-3 py-2">No active overhead ledgers</div>
                                                    )}
                                                    {overheadLedgers
                                                        .filter((l) => l.active)
                                                        .map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </Field>
                                    ) : (
                                        <Field label="General Ledger">
                                            <Select
                                                value={form.general_ledger_id}
                                                onValueChange={(v) => setForm({ ...form, general_ledger_id: v })}
                                            >
                                                <SelectTrigger className="rounded-sm" data-testid={`${moduleKey}-general-ledger-select`}>
                                                    <SelectValue placeholder="Select GL account"/>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {generalLedgers.filter((g) => g.segment === form.segment).length === 0 && (
                                                        <div className="text-[12px] text-slate-500 px-3 py-2">No GL accounts for {form.segment}</div>
                                                    )}
                                                    {generalLedgers
                                                        .filter((g) => g.segment === form.segment)
                                                        .map((g) => (
                                                            <SelectItem key={g.id} value={g.id}>
                                                                <span className="font-mono text-[10px] text-slate-500 mr-2">{g.code}</span>
                                                                {g.name}
                                                                <span className="ml-2 text-[10px] uppercase tracking-widest text-slate-400">{g.nature}</span>
                                                            </SelectItem>
                                                        ))}
                                                </SelectContent>
                                            </Select>
                                        </Field>
                                    )}
                                    <Field label="Currency">
                                        <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                                            <SelectTrigger className="rounded-sm" data-testid={`${moduleKey}-currency-select`}><SelectValue/></SelectTrigger>
                                            <SelectContent>{currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Amount">
                                        <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-sm h-9 font-mono" data-testid={`${moduleKey}-amount-input`}/>
                                    </Field>
                                    {(() => {
                                        const base = SEGMENT_BASE[form.segment] || "USD";
                                        const cur = form.currency || "—";
                                        const sameCur = cur === base;
                                        const fxLabel = sameCur
                                            ? "FX rate (n/a — same currency)"
                                            : `FX rate — ${cur} per 1 ${base}`;
                                        return (
                                            <Field label={fxLabel}>
                                                <Input
                                                    type="number"
                                                    step="0.000001"
                                                    value={form.fx_rate}
                                                    onChange={(e) => setForm({ ...form, fx_rate: e.target.value })}
                                                    className="rounded-sm h-9 font-mono"
                                                    placeholder={sameCur ? "" : `e.g. 3.68 = 3.68 ${cur} for 1 ${base}`}
                                                    disabled={sameCur}
                                                    data-testid={`${moduleKey}-fx-input`}
                                                />
                                            </Field>
                                        );
                                    })()}
                                    <Field label="Bank date (cleared on)">
                                        <Input type="date" value={form.bank_date} onChange={(e) => setForm({ ...form, bank_date: e.target.value })} className="rounded-sm h-9 font-mono" data-testid={`${moduleKey}-bank-date-input`}/>
                                    </Field>
                                    <div className="col-span-2">
                                        <Field label="Remarks">
                                            <Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="rounded-sm h-9" data-testid={`${moduleKey}-remarks-input`}/>
                                        </Field>
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button variant="ghost" onClick={() => closeDialog(false)}>Cancel</Button>
                                    <Button onClick={submit} className="rounded-sm bg-[#002FA7]" data-testid={`${moduleKey}-submit-btn`}>{editId ? "Save changes" : "Create"}</Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    ) : null
                }
            />

            <div className="flex flex-wrap items-center gap-3 mb-3">
                <PeriodFilter initialPreset="this_month" onChange={setPeriod}/>
            </div>

            <div className="bg-white border border-slate-200 overflow-x-auto">
                <table className="w-full text-[13px] financial min-w-[900px]">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                            <th className="px-3 py-2.5">Date</th>
                            <th className="px-3 py-2.5">{titleSingular} #</th>
                            <th className="px-3 py-2.5">Entity</th>
                            <th className="px-3 py-2.5">Segment</th>
                            <th className="px-3 py-2.5">Bank</th>
                            <th className="px-3 py-2.5">{isReceipt ? "Partner" : "Pay to"}</th>
                            <th className="px-3 py-2.5 num">Amount</th>
                            <th className="px-3 py-2.5">Cur</th>
                            <th className="px-3 py-2.5 num">Base</th>
                            <th className="px-3 py-2.5 w-24"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && <tr><td colSpan={10} className="text-center text-slate-500 py-10">No {titleSingular.toLowerCase()}s</td></tr>}
                        {rows.map((r) => (
                            <tr key={r.id} className="border-b border-slate-100" data-testid={`${moduleKey}-row-${r.id}`}>
                                <td className="px-3 py-2 font-mono">{r.date}</td>
                                <td className="px-3 py-2 font-mono">{r[numField]}</td>
                                <td className="px-3 py-2">{entityName(r.entity_id)}</td>
                                <td className="px-3 py-2"><SegBadge segment={r.segment}/></td>
                                <td className="px-3 py-2">{bankName(r.bank_account_id)}</td>
                                <td className="px-3 py-2">
                                    {r.partner_name ? (
                                        r.partner_name
                                    ) : r.overhead_ledger_name ? (
                                        <span className="inline-flex items-center gap-1.5">
                                            <span className="text-[10px] uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">
                                                Overhead
                                            </span>
                                            <span className="text-slate-700">{r.overhead_ledger_name}</span>
                                        </span>
                                    ) : r.general_ledger_name ? (
                                        <span className="inline-flex items-center gap-1.5">
                                            <span className="text-[10px] uppercase tracking-widest text-[#002FA7] bg-blue-50 border border-blue-200 px-1.5 py-0.5">
                                                GL{r.general_ledger_nature ? ` · ${r.general_ledger_nature}` : ""}
                                            </span>
                                            <span className="text-slate-700">{r.general_ledger_name}</span>
                                        </span>
                                    ) : (
                                        "—"
                                    )}
                                </td>
                                <td className="px-3 py-2 num">{Number(r.amount).toFixed(2)}</td>
                                <td className="px-3 py-2 font-mono">{r.currency}</td>
                                <td className="px-3 py-2 num font-semibold">{Number(r.amount_base).toFixed(2)} <span className="text-[10px] text-slate-400">{r.base_currency}</span></td>
                                <td className="px-3 py-2 whitespace-nowrap text-right">
                                    {canWrite && (
                                        <>
                                            <Button variant="ghost" size="sm" onClick={() => startEdit(r)} className="h-7 px-2" data-testid={`${moduleKey}-edit-${r.id}`}><Pencil size={13}/></Button>
                                            <Button variant="ghost" size="sm" onClick={() => remove(r.id)} className="h-7 px-2 text-red-600" data-testid={`${moduleKey}-delete-${r.id}`}><Trash2 size={13}/></Button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export function ReceiptsPage() {
    return <VoucherPage kind="receipts" />;
}
export function PaymentsPage() {
    return <VoucherPage kind="payments" />;
}
